import { apiOrchestrator } from './apiService';

export interface ValidateRecoveryCodeResponse {
  success: boolean;
  email?: string;
  partial_email?: string;
  message?: string;
}

// Backend response structure
interface BackendValidationResponse {
  valid: boolean;
  message: string;
  recovery_code_id: number | null;
  email?: string;
  partial_email?: string;
}

// Recovery code details response
interface RecoveryCodeDetailsResponse {
  id: number;
  user_id: number;
  code: string;
  email: string;
  partial_email: string;
  whatsapp_number: string;
  expires_at: string;
  used_at: string | null;
  created_at: string;
}

/**
 * Validate recovery code entered by user
 * Uses the /recovery-codes/validate-by-code endpoint
 * Full path: /orchestrator-policy-role/v2/recovery-codes/validate-by-code
 */
export const validateRecoveryCode = async (
  code: string
): Promise<ValidateRecoveryCodeResponse> => {
  try {
    const endpoint = '/recovery-codes/validate-by-code';

    const response = await apiOrchestrator.post<BackendValidationResponse>(endpoint, { code });

    // Backend returns 200 OK even for invalid codes
    // Check the 'valid' field to determine success
    if (!response.data.valid) {
      console.log('[RecoveryCodeService] ===== VALIDATION FAILED =====');
      console.log('[RecoveryCodeService] Backend message:', response.data.message);
      return {
        success: false,
        message: response.data.message || 'Código inválido ou expirado',
      };
    }

    // Valid code - check if email is in response
    let email = response.data.email || response.data.partial_email;
    let partial_email = response.data.partial_email;

    // If email is not in validation response, fetch it using recovery_code_id
    if (!email && response.data.recovery_code_id) {
      console.log('[RecoveryCodeService] Email not in validation response, fetching details...');
      try {
        const detailsResponse = await apiOrchestrator.get<RecoveryCodeDetailsResponse>(
          `/recovery-codes/${response.data.recovery_code_id}`
        );
        email = detailsResponse.data.email || detailsResponse.data.partial_email;
        partial_email = detailsResponse.data.partial_email;
      } catch (detailsError) {
        console.error('[RecoveryCodeService] Error fetching recovery code details:', detailsError);
        // Continue with validation success even if we can't get the email
      }
    }

    // Valid code - return email information
    return {
      success: true,
      email: email,
      partial_email: partial_email,
      message: response.data.message || 'Código validado com sucesso',
    };
  } catch (error: any) {
    console.error('[RecoveryCodeService] ===== VALIDATION ERROR =====');
    console.error('[RecoveryCodeService] Error object:', error);
    console.error('[RecoveryCodeService] Error response status:', error.response?.status);
    console.error('[RecoveryCodeService] Error response data:', error.response?.data);
    console.error('[RecoveryCodeService] Error response headers:', error.response?.headers);
    console.error('[RecoveryCodeService] Error message:', error.message);
    console.error('[RecoveryCodeService] Request config:', {
      url: error.config?.url,
      method: error.config?.method,
      baseURL: error.config?.baseURL,
      data: error.config?.data,
    });

    // Extract error message from response
    const errorMessage =
      error.response?.data?.detail ||
      error.response?.data?.message ||
      'Código inválido ou expirado';

    console.error('[RecoveryCodeService] Final error message:', errorMessage);

    return {
      success: false,
      message: errorMessage,
    };
  }
};
